import { useTranslation } from 'react-i18next';
import { Checkbox, Chip, Segmented, SelectField, TextArea, TextField } from '@sauda/ui/kit';
import { FURNISHINGS } from '@sauda/shared';
import {
  AMENITY_CHOICES,
  BHK_CHOICES,
  type BasicsProblems,
  type PropertyDraft,
} from '../../lib/property-form.js';
import { ListingFactsFields } from './ListingFactsFields.js';

interface Props {
  draft: PropertyDraft;
  onChange: (patch: Partial<PropertyDraft>) => void;
}

/** Title, BHK, the amount in the large money field, deposit, maintenance and place. */
export function BasicsFields({ draft, onChange, problems }: Props & { problems: BasicsProblems }) {
  const { t } = useTranslation();
  const problem = (key: keyof BasicsProblems) =>
    problems[key] === undefined ? undefined : t(`property.problem_${problems[key]}`);
  return (
    <div className="form-stack">
      <TextField
        label={t('property.title')}
        maxLength={120}
        value={draft.title}
        error={problem('title')}
        onChange={(e) => onChange({ title: e.target.value })}
        required
      />
      <div role="group" aria-label={t('property.bhk')} className="form-group">
        <p className="k-t-label">{t('property.bhk')}</p>
        <div className="chips-row">
          {BHK_CHOICES.map((choice) => (
            <Chip
              key={choice}
              selected={draft.bhk === choice}
              onClick={() => onChange({ bhk: draft.bhk === choice ? '' : choice })}
            >
              {choice}
            </Chip>
          ))}
        </div>
      </div>
      <TextField
        money
        className="k-field--display"
        label={draft.kind === 'rent' ? t('property.rent') : t('property.price')}
        prefix="₹"
        inputMode="decimal"
        value={draft.money}
        error={problem('money')}
        onChange={(e) => onChange({ money: e.target.value })}
        required
      />
      <div className="form-pair">
        <TextField
          label={t('property.deposit')}
          prefix="₹"
          inputMode="decimal"
          value={draft.deposit}
          error={problem('deposit')}
          onChange={(e) => onChange({ deposit: e.target.value })}
        />
        <TextField
          label={t('property.maintenance')}
          prefix="₹"
          inputMode="decimal"
          value={draft.maintenance}
          error={problem('maintenance')}
          onChange={(e) => onChange({ maintenance: e.target.value })}
        />
      </div>
      <TextField
        label={t('property.locality')}
        value={draft.locality}
        onChange={(e) => onChange({ locality: e.target.value })}
      />
    </div>
  );
}

/** Public details first, then what only the team sees. */
export function DetailsFields({ draft, onChange }: Props) {
  const { t } = useTranslation();
  const toggle = (name: string) =>
    onChange({
      amenities: draft.amenities.includes(name)
        ? draft.amenities.filter((a) => a !== name)
        : [...draft.amenities, name],
    });
  const extra = draft.amenities.filter((a) => !(AMENITY_CHOICES as readonly string[]).includes(a));
  return (
    <div className="form-stack">
      <h2 className="k-t-title-sm">{t('property.publicFacts')}</h2>
      <TextField
        label={t('property.society')}
        value={draft.society}
        onChange={(e) => onChange({ society: e.target.value })}
      />
      <div className="form-pair">
        <TextField
          label={t('property.flatNo')}
          value={draft.flatNo}
          onChange={(e) => onChange({ flatNo: e.target.value })}
        />
        <TextField
          label={t('property.floor')}
          value={draft.floor}
          onChange={(e) => onChange({ floor: e.target.value })}
        />
      </div>
      <TextField
        label={t('property.carpet')}
        inputMode="numeric"
        value={draft.carpet}
        onChange={(e) => onChange({ carpet: e.target.value.replace(/\D/g, '') })}
      />
      <SelectField
        label={t('property.furnishing')}
        value={draft.furnishing}
        onChange={(e) => onChange({ furnishing: e.target.value as PropertyDraft['furnishing'] })}
        options={[
          { value: '', label: t('property.notSet') },
          ...FURNISHINGS.map((f) => ({ value: f, label: t(`property.furnishing_${f}`) })),
        ]}
      />
      <div role="group" aria-label={t('property.amenitiesLabel')} className="form-group">
        <p className="k-t-label">{t('property.amenitiesLabel')}</p>
        <div className="chips-row">
          {[...AMENITY_CHOICES, ...extra].map((name) => (
            <Chip key={name} selected={draft.amenities.includes(name)} onClick={() => toggle(name)}>
              {name}
            </Chip>
          ))}
        </div>
      </div>
      <ListingFactsFields draft={draft} onChange={onChange} />
      <h2 className="k-t-title-sm">{t('property.teamOnly')}</h2>
      <p className="k-t-body-sm k-muted">{t('property.teamOnlyHelp')}</p>
      <TextField
        label={t('property.ownerName')}
        value={draft.ownerName}
        onChange={(e) => onChange({ ownerName: e.target.value })}
      />
      <TextField
        label={t('property.ownerPhone')}
        type="tel"
        value={draft.ownerPhone}
        onChange={(e) => onChange({ ownerPhone: e.target.value })}
      />
      <span className="k-t-label-lg">{t('property.ownerLang')}</span>
      <Segmented
        label={t('property.ownerLang')}
        value={draft.ownerLang}
        onChange={(v) => onChange({ ownerLang: v as PropertyDraft['ownerLang'] })}
        options={[
          { value: 'en', label: 'English' },
          { value: 'mr', label: 'मराठी' },
          { value: 'hi', label: 'हिन्दी' },
        ]}
      />
      <Checkbox
        label={t('property.tellOwner')}
        checked={draft.tellOwner}
        onChange={(e) => onChange({ tellOwner: e.target.checked })}
      />
      <TextArea
        label={t('property.notes')}
        value={draft.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
      />
    </div>
  );
}
